import { TextField } from '@medly-components/core';
import { PASSWORD } from 'constants/onboarding';
import React, { useCallback, useEffect, useState } from 'react';
import { passwordValidation } from '../../../utils/validations';
import Styled from '../Styled/Styled';
import PasswordStrengthBar from './PasswordStrengthBar';
import { PasswordSuffix } from './PasswordSuffix';

/**
 * PasswordField Component Props
 * @typedef {object} Props
 * @property {function(string): void} onChange - Callback function invoked when the password value changes.
 * @property {function(): void} [onBlur] - Optional callback function invoked when the input field loses focus.
 * @property {string} [label] - Optional label text for the password field.
 * @property {string} [id] - Optional unique identifier for the password field, used as the 'id' attribute in the HTML element.
 */
interface Props {
    onChange: (password: string) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
    label?: string;
    id?: string;
    isConfirmPassword?: boolean;
    hasValidator?: boolean;
    value?: string;
    autoComplete?: string;
    disabled?: boolean;
    onScoreChange?: (score: number) => void;
}

/**
 * PasswordField Component.
 * A reusable password input field with validation capabilities.
 * @param {Props} props - The props for the PasswordField component.
 */
export const Password = ({
    onChange,
    id = 'password',
    onBlur = () => {
        return;
    },
    onKeyDown = () => {
        return;
    },
    label = PASSWORD,
    isConfirmPassword = false,
    hasValidator = true,
    value = '',
    disabled = false,
    autoComplete = 'on',
    onScoreChange
}: Props) => {
    const [password, setPassword] = useState(value);
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    //handle password change
    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setPassword(e.target.value);
            onChange(e.target.value);
            setPasswordError('');
        },
        [setPassword, onChange, setPasswordError]
    );

    const onPasswordBlur = useCallback(() => {
        onBlur();
        if (hasValidator) {
            const passwordErrorMsg = passwordValidation({ isConfirmPassword })(password);
            setPasswordError(passwordErrorMsg);
        }
    }, [setPasswordError, passwordValidation, isConfirmPassword, hasValidator, onBlur, password]);

    const PasswordSuffixComponent = () => {
        if (!password) {
            return <></>;
        }
        return <PasswordSuffix showPassword={showPassword} setShowPassword={setShowPassword} />;
    };

    useEffect(() => {
        setPassword(value);
    }, [value]);

    return (
        <Styled.NoBgBox px={0} py={0} my={0} mx={0} display="flex" style={{ flexDirection: 'column', gap: '0.8rem' }}>
            <TextField
                autoComplete={autoComplete}
                fullWidth
                id={id}
                label={label}
                name="password"
                onChange={handleChange}
                size="M"
                onBlur={onPasswordBlur}
                onKeyDown={onKeyDown}
                type={showPassword ? 'text' : 'password'}
                value={password}
                variant="outlined"
                suffix={PasswordSuffixComponent}
                errorText={passwordError}
                disabled={disabled}
            />
            {hasValidator && !isConfirmPassword ? <PasswordStrengthBar password={password} onChangeScore={onScoreChange} /> : null}
        </Styled.NoBgBox>
    );
};
